interface FAQItem {
  q: string;
  a: string;
}

interface ServiceSchemaItem {
  name: string;
  description: string;
}

interface AreaServedItem {
  "@type": string;
  name: string;
}

interface CitySEOHeadProps {
  city: string;
  state: string;
  stateAbbr: string;
  route: string;
  title: string;
  description: string;
  geoPlacename: string;
  geoPosition: string;
  faqs: FAQItem[];
  servicesSchema: ServiceSchemaItem[];
  statePageName?: string;
  statePageRoute?: string;
  lastModified?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  areaServedList?: AreaServedItem[];
  serviceAreaDesc?: string;
}

export default function CitySEOHead({
  city,
  state,
  stateAbbr,
  route,
  title,
  description,
  geoPlacename,
  geoPosition,
  faqs,
  servicesSchema,
  statePageName,
  statePageRoute,
  lastModified,
  keywords,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  areaServedList,
  serviceAreaDesc,
}: CitySEOHeadProps) {
  const pageUrl = `https://fastpassportcenter.com${route}/`;

  const ogTitleFinal = ogTitle || title;
  const ogDescFinal = ogDescription || description;
  const twitterTitleFinal = twitterTitle || ogTitleFinal;
  const twitterDescFinal = twitterDescription || ogDescFinal;

  const areaServed = areaServedList && areaServedList.length > 0
    ? areaServedList
    : { "@type": "City", name: city, containedInPlace: { "@type": "State", name: state } };

  const localDesc = serviceAreaDesc || `Fast Passport Center does not have a physical office in ${city} — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for ${city} and ${state} travelers.`;

  const graph = [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        ...(statePageName && statePageRoute
          ? [{ "@type": "ListItem", position: 2, name: statePageName, item: `https://fastpassportcenter.com${statePageRoute}/` },
             { "@type": "ListItem", position: 3, name: `${city} Passport Services`, item: pageUrl }]
          : [{ "@type": "ListItem", position: 2, name: `${city} Passport Services`, item: pageUrl }]
        ),
      ],
    },
    {
      "@type": "LocalBusiness",
      name: "Fast Passport Center",
      description: localDesc,
      url: pageUrl,
      telephone: "+1-877-253-0084",
      areaServed,
      ...(areaServedList && areaServedList.length > 0
        ? { containedInPlace: { "@type": "State", name: state } }
        : {}),
      priceRange: "$$$",
      openingHours: ["Mo-Fr 09:00-18:00"],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "1200",
      },
    },
    ...servicesSchema.map((s) => ({
      "@type": "Service",
      serviceType: s.name,
      provider: {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
      },
      areaServed: areaServedList && areaServedList.length > 0
        ? areaServedList
        : { "@type": "City", name: city },
      description: s.description,
    })),
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.a,
        },
      })),
    },
  ];

  const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {lastModified && <meta name="last-modified" content={lastModified} />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={ogDescFinal} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Fast Passport Center" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@fastpassportcenter" />
      <meta name="twitter:title" content={twitterTitleFinal} />
      <meta name="twitter:description" content={twitterDescFinal} />
      <meta name="geo.placename" content={geoPlacename} />
      <meta name="geo.region" content={`US-${stateAbbr}`} />
      <meta name="geo.position" content={geoPosition} />
      <meta name="ICBM" content={geoPosition.replace(";", ", ")} />
      <link rel="canonical" href={pageUrl} />
      <script
        type="application/ld+json"
        id={`structured-data-${route.replace(/\//g, "-")}`}
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
    </>
  );
}